// OWNED BY the timeline implementation agent — only that agent edits this file
// (plus files it creates under detail/tabs/timeline/). Everything else under
// detail/ (registry, tab strip, header, page shell, sessionCache) is frozen
// shared foundation — see docs/crm/customer-page-implementation.md (server repo).
//
// ציר זמן (mock s17). The FEED itself now lives in ./timeline/TimelineSurface,
// because the CRM agency-person panel renders the same one; this file is the
// contact page's host for it — it answers the two questions the surface cannot,
// and nothing else.
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import useOrgDirectory from "hooks/useOrgDirectory";
import useCrmMyAccess from "hooks/crm/useCrmMyAccess";
import { CRM_LAST_ORG_STORAGE_KEY, resolveCrmOrgId } from "../../../crm/crmView";
import useContactAgency from "../../../crm/panel/agency/useContactAgency";
import { TimelineSurface } from "./timeline/TimelineSurface";
import { preloadTimelineData } from "./timeline/timelineData";
import type { RealEstateContact } from "../../../../../@types/sharedSchema";
import type { ContactTabComponentProps, TabBadge } from "./tabContract";

// Same value as ContactDetailTabs' CONTACT_DETAIL_TAB_PARAM — duplicated here
// (not imported) to avoid a module cycle: ContactDetailTabs → registry → this file.
const TAB_PARAM = "tab";

export default function TimelineTab({ contact }: ContactTabComponentProps): JSX.Element {
  const contactId = contact.id ?? "";
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * WHICH AGENCY THIS CONTACT'S "שמור אצל" IS ABOUT.
   *
   * The CRM page has a picker for this; the contact page has nowhere to put one and no
   * reason to — a person's history is not a per-agency question until you ask who holds
   * them. So it reuses the CRM page's own answer: the last agency the realtor chose there,
   * validated against current membership, falling back to the one they administer.
   *
   * `resolveCrmOrgId` does the validating, and that matters more than it looks: a stored id
   * left behind by a membership that has ended would otherwise 403 every holders request on
   * every contact anyone opens, forever, with no picker on screen to correct it.
   *
   * Both hooks below are module-cached and persisted to `localStorage`, so on a warm app
   * this whole resolution is synchronous and costs no request.
   */
  const { orgs } = useOrgDirectory();
  const [lastOrgId] = useLocalStorage<string>(CRM_LAST_ORG_STORAGE_KEY, "");
  const orgId = useMemo(() => resolveCrmOrgId(lastOrgId || null, orgs), [lastOrgId, orgs]);

  /**
   * ⚠️ `browse` IS THE GATE, and it is the same one the CRM list uses.
   *
   * "Three colleagues also hold this person" is a fact about the agency's BOOK, which is
   * exactly what the "מאגר משרדי" permission governs — not about this contact document,
   * which the caller already owns. So a member without it gets this tab precisely as it was
   * before the agency block existed, which is also the fleet default (member.browse = false)
   * and therefore what almost everyone sees.
   *
   * While the permission is still unknown, `allowed` is false and nothing is requested. That
   * is the right direction to be wrong in: the block appears a beat late rather than
   * flashing and being taken away.
   *
   * And a realtor in NO agency asks nothing at all — `enabled: false`. Measured 2026-08-16,
   * that is 4,939 of 5,226 users, and this page is one of the most-visited in the product;
   * a permission lookup that can only 403 is not worth a round trip per session for them.
   */
  const access = useCrmMyAccess(orgId ?? undefined, { enabled: orgs.length > 0 });
  const agency = useContactAgency({
    contactId,
    orgId,
    allowed: access.canBrowseOrg && access.canSeePool
  });

  const openSignaturesTab = useCallback((): void => {
    // MERGE the current search params — never rebuild (the ?tab= deep link and the global
    // signature dialog share the URL).
    const next = new URLSearchParams(searchParams);
    next.set(TAB_PARAM, "signatures");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <TimelineSurface
      contactId={contactId}
      agency={agency}
      /* Both off: this tab is the person's whole history, not one agency's view of it. See
         `TimelineSurface`'s header for what each would do. */
      restrictToAgency={false}
      showAgencyWhenSolo={false}
      onOpenSignaturesTab={openSignaturesTab}
    />
  );
}

// Badge for the tab strip (hook — reads only from cache-backed state). The
// timeline has no meaningful "count" badge by design — return null.
export function useTimelineTabBadge(_contact: RealEstateContact): TabBadge {
  return null;
}

// Cache warm-up fired on contacts-table row hover and page mount: warms the
// SHARED 'contact-details:{id}' key (dedup + stale-while-revalidate via
// fetchCached — idempotent-cheap by construction). Never throws.
export function preloadTimelineTab(contact: RealEstateContact): void {
  preloadTimelineData(contact);
}
